import { useEffect, useState } from "react";

/**
 * Encode a single path segment for S3/CDN URLs.
 * Spaces, parentheses, and other reserved chars become %XX.
 */
function encodePathSegment(seg) {
  if (!seg) return seg;
  let decoded = seg;
  try {
    decoded = decodeURIComponent(seg);
  } catch {
    decoded = seg;
  }
  return encodeURIComponent(decoded);
}

function encodePathSegments(relativePath) {
  return String(relativePath)
    .split("/")
    .map(encodePathSegment)
    .join("/");
}

/**
 * Build a safe URL for theme images (S3 keys under REACT_APP_S3_PATH, or full https URLs).
 */
export function getThemeAssetUrl(imgPath) {
  if (!imgPath) return "";
  const s = String(imgPath).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const encodedPath = encodePathSegments(u.pathname.replace(/^\/+/, ""));
      return `${u.origin}/${encodedPath}${u.search || ""}${u.hash || ""}`;
    } catch {
      return s;
    }
  }
  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/?$/, "/");
  const path = s.replace(/^\/+/, "");
  return `${base}${encodePathSegments(path)}`;
}

/**
 * Value for CSS `background-image`. Unquoted url(https://.../a copy.png) is invalid.
 */
export function toCssUrlValue(href) {
  if (!href) return "none";
  const escaped = String(href).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

/**
 * Pick `background_mob` vs `background_desk` from theme data.
 * Portrait mobile art must not be used in landscape.
 */
export function themeBackgroundKeyForViewport(themeData) {
  if (!themeData || typeof themeData !== "object") return null;
  const w = typeof window !== "undefined" ? window.innerWidth : 0;
  const narrow = w > 0 && w <= 768;
  const portrait =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: portrait)").matches;
  const mob = themeData.background_mob;
  const desk = themeData.background_desk;
  if (narrow && portrait) return mob || desk || null;
  return desk || mob || null;
}

/** Bundled defaults served from /public/img, used when a theme image is missing or fails to load. */
export const FALLBACK_TICKET_IMAGE = `${process.env.PUBLIC_URL || ""}/img/ticket.png`;
export const FALLBACK_LUCKYLOGO_IMAGE = `${process.env.PUBLIC_URL || ""}/img/feeling_lucky.png`;

/**
 * Resolves to `url` once it loads, otherwise to `fallback` (empty url, 404, network error).
 * Works for <img src> and CSS background-image alike.
 */
export function useImageWithFallback(url, fallback) {
  const [src, setSrc] = useState(url || fallback);

  useEffect(() => {
    if (!url) {
      setSrc(fallback);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => !cancelled && setSrc(url);
    img.onerror = () => !cancelled && setSrc(fallback);
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [url, fallback]);

  return src;
}
